import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { FormPanelButtonType, FormRendererAPIType } from 'components/common/form';
import FormPanelButtonUpdateIndexes from 'components/common/form/top-control-panel/button-components/FormPanelButtonUpdateIndexes';
import { Box } from '@mui/material';
import useBundleTranslation from 'i18n';
import Alert from '@mui/material/Alert';
import IconHandler from 'components/common/icon/IconHandler';
import { TFunction } from 'i18next';
import { useConfigConstantValue } from 'hooks/useConfigConstant';

export type InfoMessageType = 'none' | 'optimized' | 'full' | 'access';

export function getReIndexRequiredContent(t: TFunction, messageType: InfoMessageType, confVar: string) {
    if (confVar == 'full') {
        return (
            <Box>
                <Box sx={{ fontWeight: 'bold' }}>{t('update_index_confirmation.full_config.sub_title')}</Box>
                {t('update_index_confirmation.full_config.content')}
            </Box>
        );
    }

    if (messageType == 'none') {
        return <Box></Box>;
    }
    const subTitle = t(`update_index_confirmation.${messageType}.sub_title`);
    return (
        <Box>
            {subTitle.length > 0 ? <Box sx={{ fontWeight: 'bold' }}>{subTitle}</Box> : null}{' '}
            {t(`update_index_confirmation.${messageType}.content`)}
        </Box>
    );
}

export function getReIndexRequiredTitle(t: TFunction, messageType: InfoMessageType, confVar: string) {
    if (confVar == 'full') {
        return t('update_index_confirmation.full_config.title');
    }
    if (messageType == 'none') {
        return '';
    }
    return t(`update_index_confirmation.${messageType}.title`);
}

export function ReIndexRequiredLine({
    messageType,
    form,
    resetFields,
    autoOpen,
}: {
    messageType: InfoMessageType;
    form: FormRendererAPIType;
    resetFields: Array<string>;
    autoOpen?: boolean;
}) {
    const triggerRef = useRef<any>();
    const [wasAutoOpen, setWasAutoOpen] = useState(false);
    useEffect(() => {
        if (!triggerRef.current) {
            return;
        }
        if (autoOpen && !wasAutoOpen) {
            triggerRef.current.click();
            setWasAutoOpen(true);
        }
    }, [triggerRef.current, messageType, autoOpen]);
    if (messageType == 'none') {
        return null;
    }

    return <PopupWrapper triggerRef={triggerRef} messageType={messageType} form={form} resetFields={resetFields} />;
}

function PopupWrapper({
    triggerRef,
    messageType,
    form,
    resetFields,
}: {
    triggerRef: MutableRefObject<any>;
    messageType: InfoMessageType;
    form: FormRendererAPIType;
    resetFields: Array<string>;
}) {
    const label =
        messageType == 'access'
            ? 'update_index_confirmation.warn_message.part_0a'
            : 'update_index_confirmation.warn_message.part_0';
    const { t } = useBundleTranslation('components/common/form/form');

    const confVar: string = useConfigConstantValue('GLOBAL_SEARCH_DATA_ANALYZER_INDEX_TYPE');

    return (
        <Alert
            severity={'error'}
            icon={<IconHandler icon={{ type: 'mi', value: 'alert' }} />}
            variant={'standard'}
            sx={{ justifyContent: 'center' }}
        >
            {t(label)}
            <FormPanelButtonUpdateIndexes
                confirmationPopupContent={getReIndexRequiredContent(t, messageType, confVar)}
                confirmationPopupTitle={getReIndexRequiredTitle(t, messageType, confVar)}
                extraUrlParams={messageType == 'full' ? { force: '1' } : undefined}
                showProgress={true}
                button={
                    {
                        label: t('update_index_confirmation.warn_message.part_1'),
                        component: 'FormPanelButtonUpdateIndexes',
                        translationNS: 'app/admin/search',
                        props: {
                            alert_flag_component_name: resetFields,
                            getIndexStatusUrl: '/data/admin/search/get-index-status',
                            updateIndexUrl: '/data/admin/search/update-index',
                            cancelIndexUpdateUrl: '/data/admin/search/cancel-index-update',
                            isLink: true,
                        },
                    } as FormPanelButtonType<any>
                }
                buttonName={'updateIndex'}
                formActions={form}
                submitId={''}
                isSubMenuItem={false}
            />
            {t('update_index_confirmation.warn_message.part_2')}

            {/*Auto Open Popup*/}
            <FormPanelButtonUpdateIndexes
                confirmationPopupContent={getReIndexRequiredContent(t, 'full', confVar)}
                confirmationPopupTitle={getReIndexRequiredTitle(t, 'full', confVar)}
                triggerRef={triggerRef}
                extraUrlParams={{ force: '1' }}
                showProgress={false}
                button={
                    {
                        label: '',
                        component: 'FormPanelButtonUpdateIndexes',
                        translationNS: 'app/admin/search',
                        props: {
                            alert_flag_component_name: resetFields,
                            getIndexStatusUrl: '/data/admin/search/get-index-status',
                            updateIndexUrl: '/data/admin/search/update-index',
                            cancelIndexUpdateUrl: '/data/admin/search/cancel-index-update',
                            isLink: true,
                        },
                    } as FormPanelButtonType<any>
                }
                buttonName={'updateIndex'}
                formActions={form}
                submitId={''}
                isSubMenuItem={false}
            />
        </Alert>
    );
}
